<template>
    <b-card :header="uLang('app.data_export')" header-tag="h4" class="bg-default-card">
        <form @submit.prevent="onSubmit">
            <fieldset :disabled="busy">
                <div class="form-group data-export-dates">
                    <label class="col-form-label" for="date-from">{{ uLang('app.start_date') }}</label>
                    <vue-pikaday
                        id="date-from"
                        v-model="formData.date_from"
                        :options="pickerOptions"
                        class="form-control data-export-date"
                        autocomplete="off"
                    />

                    <label class="col-form-label" for="date-to">{{ uLang('app.end_date') }}</label>
                    <vue-pikaday
                        id="date-to"
                        :key="'date-to-' + (formData.date_from || 'open')"
                        v-model="formData.date_to"
                        :options="endPickerOptions"
                        class="form-control data-export-date"
                        autocomplete="off"
                    />
                </div>

                <div v-for="category in categories" :key="category.key" class="mb-4">
                    <h5>{{ category.label }}</h5>
                    <label class="custom-control custom-checkbox mb-2">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            :checked="allSelected(category)"
                            :ref="'category-' + category.key"
                            @change="toggleCategory(category)"
                        >
                        <span class="custom-control-indicator"></span>
                        <span class="custom-control-description">{{ uLang('app.data_export_all') }}</span>
                    </label>
                    <div class="row">
                        <div
                            v-for="table in category.tables"
                            :key="table.key"
                            class="col-md-4 mb-2"
                        >
                            <label class="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    class="custom-control-input"
                                    :value="table.key"
                                    v-model="selected"
                                >
                                <span class="custom-control-indicator"></span>
                                <span class="custom-control-description">{{ table.label }}</span>
                            </label>
                        </div>
                    </div>
                </div>
            </fieldset>

            <div class="form-group fr-absolute">
                <input
                    type="submit"
                    class="btn btn-brand"
                    :disabled="busy || selected.length === 0"
                    :value="busy ? uLang('app.data_export_busy') : uLang('app.export')"
                />
            </div>
        </form>
    </b-card>
</template>

<script>
    import { PikadayOptions } from "~/Form/elements";
    import { FileDownload } from "~/mixins";

    export default {
        name: 'DataExport',
        mixins: [FileDownload],
        data() {
            return {
                busy: false,
                categories: [],
                selected: [],
                pickerOptions: PikadayOptions,
                formData: {
                    date_from: null,
                    date_to: null,
                },
            };
        },
        computed: {
            endPickerOptions() {
                const options = Object.assign({}, PikadayOptions);

                if (this.formData.date_from) {
                    const from = moment(this.formData.date_from);
                    if (from.isValid()) {
                        options.minDate = from.toDate();
                    }
                }

                return options;
            },
        },
        watch: {
            'formData.date_from'() {
                this.clearEndWhenBeforeStart();
            },
            selected() {
                this.$nextTick(() => this.markPartialCategories());
            },
            categories() {
                this.$nextTick(() => this.markPartialCategories());
            },
        },
        created() {
            axios.get(route('reports.data-export.catalog'))
                .then(({ data }) => {
                    this.categories = data.categories;
                })
                .catch(() => {
                    this.$snotify.error(uLang('app.lack_of_permission_routes'), uLang('app.unauthorized'));
                    this.$router.push({ name: 'admin.reports.index' });
                });
        },
        methods: {
            allSelected(category) {
                return category.tables.length > 0
                    && category.tables.every((table) => this.selected.includes(table.key));
            },
            toggleCategory(category) {
                const keys = category.tables.map((table) => table.key);
                if (this.allSelected(category)) {
                    this.selected = this.selected.filter((key) => !keys.includes(key));
                    return;
                }

                this.selected = Array.from(new Set(this.selected.concat(keys)));
            },
            markPartialCategories() {
                this.categories.forEach((category) => {
                    const input = this.$refs['category-' + category.key];
                    const element = Array.isArray(input) ? input[0] : input;
                    if (!element) {
                        return;
                    }

                    const count = category.tables.filter((table) => this.selected.includes(table.key)).length;
                    element.indeterminate = count > 0 && count < category.tables.length;
                });
            },
            clearEndWhenBeforeStart() {
                if (!this.formData.date_from || !this.formData.date_to) {
                    return;
                }

                const from = moment(this.formData.date_from);
                const to = moment(this.formData.date_to);
                if (from.isValid() && to.isValid() && to.isBefore(from, 'day')) {
                    this.formData.date_to = null;
                }
            },
            isoDate(value) {
                if (!value) {
                    return null;
                }

                const parsed = moment(value);
                return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
            },
            async onSubmit() {
                this.busy = true;

                axios.post(route('reports.data-export.store'), {
                    tables: this.selected,
                    date_from: this.isoDate(this.formData.date_from),
                    date_to: this.isoDate(this.formData.date_to),
                }, {
                    responseType: 'blob',
                }).then((response) => {
                    this.downloadFile(response);
                    this.selected = [];
                    this.formData.date_from = null;
                    this.formData.date_to = null;
                    this.$snotify.info(uLang('app.data_export_downloaded'), uLang('app.data_export'));
                    this.busy = false;
                }).catch(async (error) => {
                    let message = uLang('app.data_export_failed');
                    const data = error.response && error.response.data;

                    if (data instanceof Blob) {
                        try {
                            const payload = JSON.parse(await data.text());
                            const firstError = payload.errors && Object.values(payload.errors)[0];
                            message = (firstError && firstError[0]) || payload.message || message;
                        } catch (parseError) {
                            // The response was not JSON.
                        }
                    }

                    this.$snotify.error(message, uLang('app.data_export'));
                    this.busy = false;
                });
            },
        },
    };
</script>

<style scoped>
    .data-export-dates {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 0.75rem 1.5rem;
    }

    .data-export-date {
        width: 200px;
        flex: 0 0 200px;
    }
</style>
